import * as React from "react";
import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import SeverityCircle from '../shared/SeverityCircle';
import SignalTag from '../shared/SignalTag';
import IconWrapper from '../../IconWrapper';
import { useAuth } from '../../../contexts/AuthContext';
import ConductIntelligencePanel from './ConductIntelligencePanel';
import ChatThread from '../shared/chat/ChatThread';
import OmbudsmanAdvicePanel from '../../ombudsman/OmbudsmanAdvicePanel';
import FOSOpinionPanel from '../../ombudsman/FOSOpinionPanel';
import { StartSimulationButton } from '../workspace/ombudsman/StartSimulationButton';
import { Popover, Transition } from '@headlessui/react';
import { FiInfo, FiExternalLink, FiMessageSquare } from 'react-icons/fi';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Timestamp } from '../../ui/timestamp';
import SimulationsPanel from '../ombudsman/SimulationsPanel';
import { useNavigation } from '../../../contexts/NavigationContext';
import { isOmbudsman } from '../../../utils/rbac';
import EvidenceDrawer from '../shared/EvidenceDrawer';
import { SignalActionSidebar } from './SignalActionSidebar';
import { SignalCustomerSidebar } from './SignalCustomerSidebar';
import { SignalDetailHeader } from './SignalDetailHeader';
import { SignalDetailModals } from './SignalDetailModals';
import { useSignalsLiveDetailController } from './useSignalsLiveDetailController';
import {
  getReferrerSource,
  getSignalThreadMessages,
  getSignalTitle,
  toHumanCase,
} from './signalsLiveDetailUtils';
import { processingStatusLabel, isTerminalStage } from '../../../utils/processingStatus';

/**
 * Stands in for a panel whose classification is never going to arrive.
 *
 * The panels below render a skeleton whenever the classification is absent,
 * which reads as "any moment now". For a signal in a terminal status that is
 * a promise nothing will keep.
 */
const ClassificationUnavailable: React.FC = () => (
  <p className="text-sm text-gray-500">No classification was produced for this signal.</p>
);

const SignalsLiveDetail: React.FC = () => {
  const { signalId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getBackPath, navigateWithReferrer } = useNavigation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['thread']));
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const { user } = useAuth();
  const {
    signal,
    loading,
    error,
    isConnected,
    isDeleting,
    isReclassifying,
    displaySettings,
    confirmDelete,
    handleReclassify,
  } = useSignalsLiveDetailController(signalId);

  const handleEscalate = async () => {
    // TODO: Implement escalation
  };

  const handleDownloadReport = () => {
    // TODO: Implement report download
  };

  const handleTrack = async () => {
    // TODO: Implement tracking
  };
  const handleDismiss = async () => {
    // TODO: Implement dismissal
  };

  if (loading) {
    return (
      <div className="flex h-full bg-white items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4342FF] mx-auto mb-4"></div>
          <p className="text-[#828282]" style={{ fontFamily: 'Open Sans, sans-serif' }}>Loading signal details...</p>
        </div>
      </div>
    );
  }

  if (error || !signal) {
    return (
      <div className="flex h-full bg-white items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error loading signal</h3>
          <p className="text-gray-600 mb-4">{error || 'Signal not found'}</p>
          <button 
            onClick={() => navigate('/v4/records')}
            className="px-4 py-2 bg-[#4342FF] text-white rounded-lg hover:bg-[#3332DD] transition-colors"
          >
            Back to Signals
          </button>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    const backPath = getBackPath();
    navigate(backPath);
  };

  const handleViewSimulationsInWorkspace = () => {
    if (signalId) {
      navigateWithReferrer(`/v3/ombudsman?signal_id=${signalId}`, window.location.pathname);
    }
  };

  const isOmbudsmanUser = isOmbudsman(user);
  const signalTitle = getSignalTitle(signal);
  // Absent classification means "still working" only while something is still
  // working. A terminal status says nothing is.
  const classificationUnavailable =
    !signal.classification && isTerminalStage(signal.processing_status);
  const stateReferrer = (location.state as any)?.referrer;
  const breadcrumbItems = [
    {
      label: getReferrerSource(stateReferrer),
      path: getBackPath(),
    },
    {
      label: `Signal: ${signalTitle.length > 40 ? signalTitle.substring(0, 40) + '...' : signalTitle}`,
    },
  ];

  return (
    <div className="flex flex-col h-full bg-white font-sans">
      <SignalDetailHeader
        signal={signal}
        isConnected={isConnected}
        isOmbudsmanUser={isOmbudsmanUser}
        breadcrumbItems={breadcrumbItems}
        onBack={handleBack}
        onViewSimulations={handleViewSimulationsInWorkspace}
      />

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden p-6 gap-6">
        <SignalCustomerSidebar signal={signal} />

        {/* Center Content Area */}
        <div className="flex-1 overflow-y-auto scrollbar-hide">
          {/* Signal Description Panel - Collapsible */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div
                className="overflow-hidden"
              >
              <div 
                className="p-4 flex items-center justify-between hover:bg-white/50 transition-colors cursor-pointer"
                onClick={() => {
                  const newExpanded = new Set(expandedSections);
                  if (newExpanded.has('description')) {
                    newExpanded.delete('description');
                  } else {
                    newExpanded.add('description');
                  }
                  setExpandedSections(newExpanded);
                }}
              >
                <div className="flex-1">
                  {!expandedSections.has('description') && (signal.summary_detailed || signal.verbatim) && (
                    <p className="text-figma-base text-figma-text-primary m-4 line-clamp-2">
                      {signal.summary_detailed || signal.verbatim}
                    </p>
                  )}
                </div>
                <svg 
                  className={`w-5 h-5 text-figma-text-secondary transition-transform ${expandedSections.has('description') ? 'rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24"
                >
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                </svg>
              </div>
              {expandedSections.has('description') && (
                <div className="px-6 pb-6">
                  <div className="bg-white rounded-lg p-4">
                    <div className="text-figma-base text-figma-text-primary m-4 leading-relaxed space-y-4">
                      {signal.summary_detailed && (
                        <p>{signal.summary_detailed}</p>
                      )}
                      <p>{signal.verbatim || (!signal.summary_detailed ? "No description available" : '')}</p>
                    </div>
                  </div>
                  {/* Metadata */}
                  <div className="flex items-center space-x-2 mt-4">
                    <span className="px-3 py-1 bg-white/50 text-figma-text-secondary rounded-full text-figma-xs">
                      Source: {signal.source_type || (signal.channel ? signal.channel.replace('_', ' ') : 'Unknown')}
                    </span>
                    <span className="px-3 py-1 bg-white/50 text-figma-text-secondary rounded-full text-figma-xs">
                      {processingStatusLabel(signal.processing_status) || signal.status?.toUpperCase() || 'UNFIN'}
                    </span>
                    <Timestamp
                      value={signal.received_at || signal.created_at}
                      format="relative"
                      className="px-3 py-1 bg-white/50 text-figma-text-secondary rounded-full text-figma-xs"
                    />
                  </div>
                </div>
              )}
              </div>
            </CardContent>
          </Card>

          {/* Customer Case Thread Panel - Compact with Max Height */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  Customer Case Thread
                {/* Info icon for thread metadata */}
                {(signal.external_thread_id || signal.thread_url) && (
                  <Popover className="relative ml-2">
                    <Popover.Button className="focus:outline-none">
                      {FiInfo({ className: "w-4 h-4 text-figma-text-secondary hover:text-figma-text-primary" }) as unknown as React.ReactElement}
                    </Popover.Button>
                    <Transition
                      as={React.Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-75"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1"
                    >
                      <Popover.Panel className="absolute z-10 left-1/2 -translate-x-1/2 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-sm text-gray-700">
                        {signal.external_thread_id && (
                          <div className="mb-2">
                            <span className="font-semibold">External Thread ID:</span> {signal.external_thread_id}
                          </div>
                        )}
                        {signal.thread_url && (
                          <div>
                            <a
                              href={signal.thread_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-blue-600 hover:underline"
                            >
                              View Original Thread {FiExternalLink({ className: "ml-1 w-4 h-4" }) as unknown as React.ReactElement}
                            </a>
                          </div>
                        )}
                      </Popover.Panel>
                    </Transition>
                  </Popover>
                )}
                </CardTitle>
              {(() => {
                const threadMessages = signal.thread_messages ||
                                     (Array.isArray(signal.thread_data) ? signal.thread_data : signal.thread_data?.messages) ||
                                     [];
                const messageCount = Array.isArray(threadMessages) ? threadMessages.length : 0;
                
                if (messageCount > 0) {
                  return (
                    <span className="text-figma-sm text-figma-text-secondary">
                      {messageCount} message{messageCount !== 1 ? 's' : ''}
                    </span>
                  );
                }
                return null;
              })()}
              </div>
            </CardHeader>
            <CardContent>
              <div className="max-h-96 overflow-y-auto scrollbar-hide space-y-4">
              
              {/* Thread Messages */}
              {(() => {
                const threadMessages = getSignalThreadMessages(signal);
                
                if (threadMessages.length > 0) {
                  return (
                    <ChatThread 
                      messages={threadMessages}
                      customerName={signal.customer?.name || signal.customer_name || 'Customer'}
                    />
                  );
                }
                
                // No thread messages available
                return (
                  <div className="text-center py-8">
                    <svg className="w-12 h-12 mx-auto text-figma-text-muted mb-3" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" 
                        d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                      />
                    </svg>
                    <p className="text-figma-text-secondary text-figma-base">No conversation thread available</p>
                    <p className="text-figma-text-muted text-figma-sm mt-1">This signal was received without an associated conversation</p>
                  </div>
                );
              })()}
              </div>
            </CardContent>
          </Card>

          {/* Complaint Insight Panel */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Complaint Insight</CardTitle>
            </CardHeader>
            <CardContent>
              {signal.classification && signal.classification.is_complaint !== undefined ? (
                <div className="space-y-4">
                {/* Complaint Detection Alert */}
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    <svg className={`w-5 h-5 ${signal.classification.is_complaint ? 'text-orange-500' : 'text-green-500'}`} fill="currentColor" viewBox="0 0 20 20">
                      {signal.classification.is_complaint ? (
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      ) : (
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      )}
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-semibold text-sm mb-2 ${signal.classification.is_complaint ? 'text-orange-600' : 'text-green-600'}`}>
                      {signal.classification.is_complaint ? 'Complaint Detected' : 'Not a Complaint'}
                    </h3>
                    {signal.classification.complaint_explanation && (
                      <p className="text-sm text-gray-700 mb-2">
                        {signal.classification.complaint_explanation}
                      </p>
                    )}
                    
                    {/* Confidence Score */}
                    {signal.classification.complaint_confidence !== undefined && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Confidence</p>
                        <div className="flex items-center">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                            <div 
                              className="bg-[#4342FF] h-2 rounded-full" 
                              style={{ width: `${signal.classification.complaint_confidence * 100}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600">{Math.round(signal.classification.complaint_confidence * 100)}%</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Benchmark */}
                    {signal.classification.complaint_benchmark && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Benchmark</p>
                        <p className="text-sm text-gray-700">
                          {signal.classification.complaint_benchmark}
                        </p>
                      </div>
                    )}
                    
                    {/* Category */}
                    {signal.classification.complaint_category && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Category</p>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          {signal.classification.complaint_category}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                </div>
              ) : classificationUnavailable ? (
                <ClassificationUnavailable />
              ) : (
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 bg-gray-200 rounded-full animate-pulse mt-1"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-32 mb-3 animate-pulse"></div>
                      <div className="h-3 bg-gray-200 rounded w-full mb-2 animate-pulse"></div>
                      <div className="h-3 bg-gray-200 rounded w-5/6 mb-4 animate-pulse"></div>
                      <div className="h-2 bg-gray-200 rounded w-20 mb-2 animate-pulse"></div>
                      <div className="h-2 bg-gray-200 rounded-full w-full animate-pulse"></div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          {/* Vulnerability Panel */}
          <div className="mb-6">
            <h2 className="text-figma-xl font-semibold text-figma-text-primary mb-4">Vulnerability</h2>
            <div className="bg-vanilla rounded-lg p-6">
              {signal.classification ? (
                signal.classification.vulnerability_detected ? (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-red-600 text-sm mb-2">Detected</h3>
                      {signal.classification.vulnerability_explanation && (
                        <p className="text-sm text-gray-700 mb-3">
                          {signal.classification.vulnerability_explanation}
                        </p>
                      )}
                      {signal.classification.vulnerability_labels && signal.classification.vulnerability_labels.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {signal.classification.vulnerability_labels.map((label: string, index: number) => (
                            <SignalTag key={index} label={toHumanCase(label)} variant="vulnerability" />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <svg className="w-12 h-12 mx-auto text-green-500 mb-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm text-gray-600">No vulnerability indicators detected</p>
                  </div>
                )
              ) : classificationUnavailable ? (
                <ClassificationUnavailable />
              ) : (
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-gray-200 rounded-full animate-pulse mt-1"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-20 mb-3 animate-pulse"></div>
                    <div className="h-3 bg-gray-200 rounded w-full mb-2 animate-pulse"></div>
                    <div className="h-3 bg-gray-200 rounded w-5/6 mb-4 animate-pulse"></div>
                    <div className="flex gap-2">
                      <div className="h-6 bg-gray-200 rounded-full w-24 animate-pulse"></div>
                      <div className="h-6 bg-gray-200 rounded-full w-28 animate-pulse"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Severity Panel */}
          <div className="mb-6">
            <h2 className="text-figma-xl font-semibold text-figma-text-primary mb-4">Severity</h2>
            <div className="bg-vanilla rounded-lg p-6">
              {signal.classification && (signal.classification.strain_score !== undefined || signal.classification.harm_score !== undefined) ? (
                <>
                  <div className="grid grid-cols-2 gap-8">
                  {/* Strain Block */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-figma-base font-bold text-figma-text-primary">Strain</span>
                      <span className="text-figma-2xl font-bold text-[#D4D44F]">
                        {Math.round(signal.classification.strain_score ?? 0)}
                      </span>
                    </div>
                    <div className="w-full bg-[#F5F5F5] rounded-full h-[6px] overflow-hidden">
                      <div
                        className="bg-[#D4D44F] h-[6px] transition-all duration-500 ease-out"
                        style={{ width: `${signal.classification.strain_score ?? 0}%` }}
                      />
                    </div>
                    {signal.classification.strain_summary && (
                      <p className="text-figma-base text-figma-text-primary mt-4">
                        {signal.classification.strain_summary}
                      </p>
                    )}
                    {signal.classification.strain_labels && signal.classification.strain_labels.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {signal.classification.strain_labels.map((label: string, index: number) => (
                          <SignalTag key={index} label={toHumanCase(label)} variant="strain" />
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Harm Block */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-figma-base font-bold text-figma-text-primary">Harm</span>
                      <span className="text-figma-2xl font-bold text-[#FF4343]">
                        {Math.round(signal.classification.harm_score ?? 0)}
                      </span>
                    </div>
                    <div className="w-full bg-[#F5F5F5] rounded-full h-[6px] overflow-hidden">
                      <div
                        className="bg-[#FF4343] h-[6px] transition-all duration-500 ease-out"
                        style={{ width: `${signal.classification.harm_score ?? 0}%` }}
                      />
                    </div>
                    {signal.classification.harm_summary && (
                      <p className="text-figma-base text-figma-text-primary mt-4">
                        {signal.classification.harm_summary}
                      </p>
                    )}
                    {signal.classification.harm_labels && signal.classification.harm_labels.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {signal.classification.harm_labels.map((label: string, index: number) => (
                          <SignalTag key={index} label={toHumanCase(label)} variant="harm" />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                  {/* Inline Conduct Score and Description */}
                  {(signal.conduct_score !== undefined || signal.severity_score !== undefined) && (
                    <div className="flex items-center justify-center gap-8 mt-10">
                      <SeverityCircle
                        score={signal.conduct_score ?? signal.severity_score}
                        size="large"
                        tooltip={
                          (signal.classification as any)?.severity_components?.reasoning || undefined
                        }
                      />
                      <div className="text-left max-w-md">
                        <p className="text-figma-lg font-bold text-figma-text-primary mb-1">Conduct Score</p>
                        <p className="text-figma-base text-figma-text-secondary">
                          Based on weighted indicators of strain, foreseeable harm, and scale of dissatisfaction
                        </p>
                      </div>
                    </div>
                  )}
                </>
              ) : classificationUnavailable ? (
                <ClassificationUnavailable />
              ) : (
                <div className="grid grid-cols-2 gap-8">
                  {/* Strain Skeleton */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="h-4 bg-gray-200 rounded w-12 animate-pulse"></div>
                      <div className="h-8 bg-gray-200 rounded w-12 animate-pulse"></div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-[6px] animate-pulse"></div>
                    <div className="h-3 bg-gray-200 rounded w-full mt-4 animate-pulse"></div>
                    <div className="flex gap-2 mt-4">
                      <div className="h-6 bg-gray-200 rounded-full w-20 animate-pulse"></div>
                      <div className="h-6 bg-gray-200 rounded-full w-24 animate-pulse"></div>
                    </div>
                  </div>
                  {/* Harm Skeleton */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="h-4 bg-gray-200 rounded w-12 animate-pulse"></div>
                      <div className="h-8 bg-gray-200 rounded w-12 animate-pulse"></div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-[6px] animate-pulse"></div>
                    <div className="h-3 bg-gray-200 rounded w-full mt-4 animate-pulse"></div>
                    <div className="flex gap-2 mt-4">
                      <div className="h-6 bg-gray-200 rounded-full w-24 animate-pulse"></div>
                      <div className="h-6 bg-gray-200 rounded-full w-20 animate-pulse"></div>
                    </div>
                  </div>
                  {/* Overall Score Skeleton */}
                  <div className="absolute -bottom-10 left-0">
                    <div className="w-[136px] h-[136px] bg-gray-200 rounded-full animate-pulse"></div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Outcome Assessment Panel */}
          <div className="mb-6">
            <h2 className="text-figma-xl font-semibold text-figma-text-primary mb-4">Outcome Assessment</h2>
            <div className="bg-vanilla rounded-lg p-6">
              {signal.classification && signal.classification.outcome_assessment ? (
                <>
                  <p className="text-figma-base text-figma-text-primary mb-4 leading-relaxed">
                    {signal.classification.outcome_assessment}
                  </p>
                  {signal.classification.consumer_duty_outcomes && signal.classification.consumer_duty_outcomes.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {signal.classification.consumer_duty_outcomes.map((outcome: string, index: number) => (
                        <SignalTag key={index} label={toHumanCase(outcome)} variant="outcome" />
                      ))}
                    </div>
                  )}
                </>
              ) : classificationUnavailable ? (
                <ClassificationUnavailable />
              ) : (
                <div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-2 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6 mb-2 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/6 mb-4 animate-pulse"></div>
                  <div className="flex gap-2">
                    <div className="h-6 bg-gray-200 rounded-full w-28 animate-pulse"></div>
                    <div className="h-6 bg-gray-200 rounded-full w-32 animate-pulse"></div>
                    <div className="h-6 bg-gray-200 rounded-full w-24 animate-pulse"></div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Conduct Intelligence Framework Panel */}
          <ConductIntelligencePanel classification={signal?.taxonomy_classification} />
          
          {/* Classification Feedback Button */}
          {signal?.classification?.id && signal?.taxonomy_classification?.dimensions && (
            <div className="mb-4 flex justify-end">
              <button
                onClick={() => setShowFeedbackModal(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:border-gray-400 transition-all"
              >
                <IconWrapper icon={FiMessageSquare} className="h-4 w-4" />
                Review Labels
              </button>
            </div>
          )}

          {/* Ombudsman Decisions Advisor Panel */}
          {signalId && <OmbudsmanAdvicePanel signalId={signalId} />}

          {/* Start FOS Simulation Button */}
          {signalId && (
            <div className="mb-6">
              <StartSimulationButton signalId={signalId} variant="default" />
            </div>
          )}

          {/* FOS Opinion Panel */}
          <FOSOpinionPanel fosOpinion={signal?.fos_opinion || signal?.classification?.fos_opinion} />

          {/* Simulations Panel (if ombudsman user) */}
          {isOmbudsmanUser && signalId && (
            <div id="simulations-panel" className="mt-6">
              <SimulationsPanel
                signalId={signalId}
                onCreateSimulation={(simulationId: string) => {
                  navigateWithReferrer(`/v3/ombudsman/simulations/${simulationId}`, location.pathname);
                }}
              />
            </div>
          )}
        </div>

        <SignalActionSidebar
          signal={signal}
          signalId={signalId}
          user={user}
          displaySettings={displaySettings}
          isReclassifying={isReclassifying}
          onEscalate={handleEscalate}
          onDownloadReport={handleDownloadReport}
          onTrack={handleTrack}
          onDismiss={handleDismiss}
          onReclassify={handleReclassify}
          onNavigateToTaxonomy={() => navigate(`/v3/taxonomy?signal=${signalId}`)}
        />
      </div>

      {/* Evidence Drawer - Horizontal layout at bottom */}
      {signalId && (
        <div className="border-t border-border bg-white">
          <div className="p-6">
            <h3 className="text-figma-lg font-semibold text-figma-text-primary mb-4">Evidence</h3>
            <EvidenceDrawer
              signalId={signalId}
              orientation="horizontal"
            />
          </div>
        </div>
      )}
      
      <SignalDetailModals
        signal={signal}
        signalId={signalId}
        showDeleteConfirm={showDeleteConfirm}
        isDeleting={isDeleting}
        showFeedbackModal={showFeedbackModal}
        onCancelDelete={() => setShowDeleteConfirm(false)}
        onConfirmDelete={async () => {
          await confirmDelete();
          setShowDeleteConfirm(false);
        }}
        onCloseFeedback={() => setShowFeedbackModal(false)}
      />
    </div>
  );
};

export default SignalsLiveDetail;
